'use client';

import { useSyncExternalStore } from 'react';

type Listener = () => void;

const listeners = new Set<Listener>();
let activeCategory: string | null = null;

function subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): string | null {
    return activeCategory;
}

/**
 * Always null on the server.
 *
 * The article page cannot hand its category to <Header/> through props or
 * context: (public)/layout.tsx renders <Header/> as a SIBLING of {children},
 * so nothing flows upward. A module-level store plus useSyncExternalStore is
 * the one channel that crosses that boundary. Returning null here keeps the
 * first client render identical to the SSR HTML; the underline appears on the
 * frame after hydration instead of risking a hydration mismatch.
 */
function getServerSnapshot(): string | null {
    return null;
}

export function setActiveNavCategory(slug: string | null): void {
    const next = slug ? slug : null;
    if (next === activeCategory) return;
    activeCategory = next;
    listeners.forEach((listener) => listener());
}

/**
 * Only clears when the stored value is still the one this caller set, so an
 * unmounting article page cannot wipe a value published by a newer one.
 */
export function clearActiveNavCategory(slug: string): void {
    if (activeCategory === slug) {
        setActiveNavCategory(null);
    }
}

export function useActiveNavCategory(): string | null {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}